import { useCallback, useEffect, useRef, useState } from 'react'
import { useWorkspaceStore } from '../../stores/workspaceStore'
import { governanceApi, type BrandVoice, type VoiceReview } from '../../services/api'
import { DERIVATION_WINDOW_MS, POLL_MS, refusalMessage } from './voiceReview'

/**
 * The Brand tab's own read of the voice review (MAR-384): what its section hints and its
 * overview need to know before the review card is ever on screen.
 *
 * ⚠️ Not a second owner of the card's state. `DerivedVoiceCard` keeps its own read; it REPORTS
 * every review it lands (`report`), which supersedes anything in flight here, and the tab turns
 * this hook's polling OFF while the card is mounted — so the two never poll one run twice.
 *
 * The guards are the card's, for the card's reasons: the snapshot carries its workspace and is
 * matched at the read; a request generation per workspace drops a late answer; and the poll is
 * bounded by the server's six-minute window (`DERIVATION_WINDOW_MS`) — nothing clears the
 * in-flight marker, so an unbounded poll would sit on a dead run for as long as the tab is open.
 */

export interface VoiceGlanceSnapshot {
  workspaceId: string
  /** `null` with `failed` means "could not find out", never "nothing derived". */
  review: VoiceReview | null
  failed: boolean
  /** The gated voice every consumer uses — the merchant-stated fields come from here. */
  live: BrandVoice | null
  liveFailed: boolean
}

export interface VoiceGlance {
  snapshot: VoiceGlanceSnapshot | null
  /** The poll ran past the server's window on THIS run, and the row still says `deriving`. */
  gaveUp: boolean
  /**
   * A derive in flight for the workspace on screen — this hook's OR the review card's (`track`).
   * ⚠️ One in-flight state for the tab: "Read it again" on the overview, then How we sound
   * before the 202, must not mount a card offering a second derive of its own.
   */
  deriving: boolean
  /** The last refusal of a derive, in the merchant's terms. */
  error: string | null
  derive: () => void
  report: (read: { workspaceId: string; review: VoiceReview; live: BrandVoice | null }) => void
  /** The review card's own derive, so `deriving` covers it until `done` settles. */
  track: (workspaceId: string, done: Promise<unknown>) => void
  reload: () => void
}

export default function useVoiceGlance(workspaceId: string | undefined, { poll }: { poll: boolean }): VoiceGlance {
  const [snap, setSnap] = useState<VoiceGlanceSnapshot | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<{ workspaceId: string; message: string } | null>(null)
  const [exhausted, setExhausted] = useState<{ workspaceId: string; requestedAt: string } | null>(null)
  const seq = useRef<Record<string, number>>({})

  const stillCurrent = (asked: string) => useWorkspaceStore.getState().currentWorkspace?.id === asked

  /**
   * Every read that lands here — this hook's own, or the card's through `report` — gives up on a
   * `deriving` row whose run is past the server's window. ⚠️ Not only this hook's poll can give
   * up: once How we sound has been opened the CARD is the one poller (this hook's poll is off),
   * and its last read, at the window, arrives through `report`. Without this the overview would
   * say "Reading…" for as long as the tab is open (PR #324 review 2).
   */
  const noteGiveUp = useCallback((next: VoiceGlanceSnapshot) => {
    const requestedAt = next.review?.derivationRequest?.requestedAt
    const requestedMs = Date.parse(requestedAt || '')
    if (next.review?.state === 'deriving' && Number.isFinite(requestedMs) && Date.now() - requestedMs >= DERIVATION_WINDOW_MS) {
      setExhausted({ workspaceId: next.workspaceId, requestedAt: requestedAt || '' })
    }
  }, [])

  const load = useCallback(async(asked: string) => {
    const mine = (seq.current[asked] = (seq.current[asked] ?? 0) + 1)
    const apply = (next: VoiceGlanceSnapshot) => {
      if (seq.current[asked] !== mine || !stillCurrent(asked)) return
      setSnap(next)
      noteGiveUp(next)
    }
    try {
      const [res, liveRes] = await Promise.all([
        governanceApi.getVoiceReview(asked),
        // Never allowed to reject the pair — the live voice only fills the overview's last row.
        governanceApi.getBrandVoice(asked).catch(() => ({ success: false, data: undefined }))
      ])
      const live = liveRes.success ? liveRes.data ?? null : null
      apply({
        workspaceId: asked,
        review: res.success && res.data ? res.data : null,
        failed: !(res.success && res.data),
        live,
        liveFailed: !liveRes.success
      })
    } catch {
      apply({ workspaceId: asked, review: null, failed: true, live: null, liveFailed: true })
    }
  }, [noteGiveUp])

  const [reloadToken, setReloadToken] = useState(0)
  useEffect(() => {
    if (!workspaceId) return
    void load(workspaceId)
  }, [load, workspaceId, reloadToken])

  const current = snap && snap.workspaceId === workspaceId ? snap : null
  const state = current?.review?.state
  const requestedAt = current?.review?.derivationRequest?.requestedAt
  const gaveUp = !!exhausted && exhausted.workspaceId === workspaceId && exhausted.requestedAt === (requestedAt || '')

  useEffect(() => {
    if (!poll || !workspaceId || state !== 'deriving') return
    const requestedMs = Date.parse(requestedAt || '')
    const timer = setInterval(() => {
      if (Number.isFinite(requestedMs) && Date.now() - requestedMs >= DERIVATION_WINDOW_MS) {
        clearInterval(timer)
        setExhausted({ workspaceId, requestedAt: requestedAt || '' })
        // One last read: the server flips out of `deriving` on its own once the marker ages.
        void load(workspaceId)
        return
      }
      void load(workspaceId)
    }, POLL_MS)
    return () => clearInterval(timer)
  }, [poll, workspaceId, state, requestedAt, load])

  const report = useCallback((read: { workspaceId: string; review: VoiceReview; live: BrandVoice | null }) => {
    // A settled read from the card is at least as new as anything in flight here: bump the
    // generation so a slower read of ours cannot land on top of it.
    seq.current[read.workspaceId] = (seq.current[read.workspaceId] ?? 0) + 1
    if (!stillCurrent(read.workspaceId)) return
    const next = { workspaceId: read.workspaceId, review: read.review, failed: false, live: read.live, liveFailed: !read.live }
    setSnap(next)
    noteGiveUp(next)
  }, [noteGiveUp])

  // Mirrors `busy` for the guard below: `derive` must refuse a second run before the first
  // one's state update has rendered.
  const busyRef = useRef<string | null>(null)
  const markBusy = useCallback((asked: string, done: Promise<unknown>) => {
    busyRef.current = asked
    setBusy(asked)
    const clear = () => {
      if (busyRef.current === asked) busyRef.current = null
      setBusy((b) => (b === asked ? null : b))
    }
    done.then(clear, clear)
  }, [])

  const derive = useCallback(() => {
    if (!workspaceId || busyRef.current === workspaceId) return
    const asked = workspaceId
    setError(null)
    const done = governanceApi.deriveVoice(asked)
      .then(async(res) => {
        if (!stillCurrent(asked)) return
        if (!res.success) {
          const reason = (res.details as { reason?: unknown } | undefined)?.reason
          setError({ workspaceId: asked, message: refusalMessage(reason, res.error || 'That did not go through.') })
        }
        // Re-read on success AND refusal: the state on screen must be the server's.
        await load(asked)
      })
      .catch((e) => {
        if (stillCurrent(asked)) setError({ workspaceId: asked, message: e instanceof Error ? e.message : 'That did not go through.' })
      })
    markBusy(asked, done)
  }, [workspaceId, load, markBusy])

  const reload = useCallback(() => setReloadToken((n) => n + 1), [])

  return {
    snapshot: current,
    gaveUp,
    deriving: busy === workspaceId && !!workspaceId,
    error: error && error.workspaceId === workspaceId ? error.message : null,
    derive,
    report,
    track: markBusy,
    reload
  }
}
